/**
 * Libertify Design System - Token Helpers
 *
 * Utilities to map Libertify design system sizes to Ant Design theme tokens.
 * This ensures components use theme tokens instead of hardcoded values,
 * while maintaining the size naming convention from Figma.
 */

import type { GlobalToken } from 'antd';

/**
 * Component size following Figma Design System
 * - xs: Extra small (22px height)
 * - s: Small (32px height)
 * - m: Medium (40px height) - Default
 * - l: Large (56px height)
 */
export type ComponentSize = 'xs' | 's' | 'm' | 'l';

/**
 * Get control height based on component size
 */
export const getControlHeight = (
  token: GlobalToken,
  size: ComponentSize,
): number => {
  const map: Record<ComponentSize, number> = {
    xs: token.controlHeightXS,
    s: token.controlHeightSM,
    m: token.controlHeight,
    l: token.controlHeightLG,
  };
  return map[size];
};

/**
 * Get border radius based on component size
 * Following Figma specifications for each size variant
 */
export const getBorderRadius = (
  token: GlobalToken,
  size: ComponentSize,
): number => {
  const map: Record<ComponentSize, number> = {
    xs: token.borderRadiusSM, // 4px
    s: token.borderRadius, // 8px
    m: token.borderRadius, // 8px
    l: token.borderRadiusLG, // 16px
  };
  return map[size];
};

/**
 * Get font size based on component size
 */
export const getFontSize = (
  token: GlobalToken,
  size: ComponentSize,
): number => {
  const map: Record<ComponentSize, number> = {
    xs: token.fontSizeSM - 2, // 10px (xxsmall)
    s: token.fontSizeSM, // 12px (small)
    m: token.fontSize, // 14px (medium)
    l: token.fontSizeLG, // 16px (large)
  };
  return map[size];
};

/**
 * Get horizontal padding based on component size
 */
export const getPaddingHorizontal = (
  token: GlobalToken,
  size: ComponentSize,
): number => {
  const map: Record<ComponentSize, number> = {
    xs: token.paddingXS, // 8px
    s: token.paddingSM, // 12px
    m: token.padding, // 16px
    l: token.paddingLG, // 24px
  };
  return map[size];
};

/**
 * Get vertical padding based on component size
 */
export const getPaddingVertical = (
  token: GlobalToken,
  size: ComponentSize,
): number => {
  const map: Record<ComponentSize, number> = {
    xs: token.paddingXS / 2, // 4px
    s: token.paddingXS, // 8px
    m: token.paddingXS, // 8px
    l: token.paddingSM, // 12px
  };
  return map[size];
};

/**
 * Get icon size based on component size
 */
export const getIconSize = (
  token: GlobalToken,
  size: ComponentSize,
): number => {
  const map: Record<ComponentSize, number> = {
    xs: 12,
    s: 16,
    m: 20,
    l: 24,
  };
  return map[size];
};

/**
 * Get gap between elements based on component size
 */
export const getGap = (token: GlobalToken, size: ComponentSize): number => {
  const map: Record<ComponentSize, number> = {
    xs: token.marginXXS, // 4px
    s: token.marginXXS, // 4px
    m: token.marginXS, // 8px
    l: token.marginXS, // 8px
  };
  return map[size];
};

/**
 * Token subset commonly used in styled-components
 * Use this type when passing tokens as props to avoid passing the entire GlobalToken
 */
export interface ComponentTokens {
  // Colors
  colorPrimary: string;
  colorPrimaryHover: string;
  colorPrimaryActive: string;
  colorText: string;
  colorTextSecondary: string;
  colorTextTertiary: string;
  colorTextDisabled: string;
  colorBgContainer: string;
  colorBgElevated: string;
  colorBgLayout: string;
  colorBorder: string;
  colorBorderSecondary: string;
  colorError: string;
  colorWarning: string;
  colorSuccess: string;

  // Sizing
  controlHeight: number;
  controlHeightSM: number;
  controlHeightLG: number;
  controlHeightXS: number;

  // Border Radius
  borderRadius: number;
  borderRadiusSM: number;
  borderRadiusLG: number;
  borderRadiusXS: number;

  // Typography
  fontSize: number;
  fontSizeSM: number;
  fontSizeLG: number;
  fontFamily: string;
  fontWeightStrong: number;

  // Spacing
  padding: number;
  paddingSM: number;
  paddingLG: number;
  paddingXS: number;
  paddingXXS: number;
  margin: number;
  marginSM: number;
  marginLG: number;
  marginXS: number;
  marginXXS: number;

  // Line
  lineWidth: number;
  lineWidthBold: number;

  // Shadows
  boxShadow: string;
  boxShadowSecondary: string;
  boxShadowTertiary: string;

  // Motion
  motionDurationFast: string;
  motionDurationMid: string;
  motionDurationSlow: string;
}

/**
 * Extract commonly used tokens from GlobalToken
 * Use this to avoid passing the entire token object to styled-components
 */
export const extractComponentTokens = (
  token: GlobalToken,
): ComponentTokens => ({
  colorPrimary: token.colorPrimary,
  colorPrimaryHover: token.colorPrimaryHover,
  colorPrimaryActive: token.colorPrimaryActive,
  colorText: token.colorText,
  colorTextSecondary: token.colorTextSecondary,
  colorTextTertiary: token.colorTextTertiary,
  colorTextDisabled: token.colorTextDisabled,
  colorBgContainer: token.colorBgContainer,
  colorBgElevated: token.colorBgElevated,
  colorBgLayout: token.colorBgLayout,
  colorBorder: token.colorBorder,
  colorBorderSecondary: token.colorBorderSecondary,
  colorError: token.colorError,
  colorWarning: token.colorWarning,
  colorSuccess: token.colorSuccess,
  controlHeight: token.controlHeight,
  controlHeightSM: token.controlHeightSM,
  controlHeightLG: token.controlHeightLG,
  controlHeightXS: token.controlHeightXS,
  borderRadius: token.borderRadius,
  borderRadiusSM: token.borderRadiusSM,
  borderRadiusLG: token.borderRadiusLG,
  borderRadiusXS: token.borderRadiusXS,
  fontSize: token.fontSize,
  fontSizeSM: token.fontSizeSM,
  fontSizeLG: token.fontSizeLG,
  fontFamily: token.fontFamily,
  fontWeightStrong: token.fontWeightStrong,
  padding: token.padding,
  paddingSM: token.paddingSM,
  paddingLG: token.paddingLG,
  paddingXS: token.paddingXS,
  paddingXXS: token.paddingXXS,
  margin: token.margin,
  marginSM: token.marginSM,
  marginLG: token.marginLG,
  marginXS: token.marginXS,
  marginXXS: token.marginXXS,
  lineWidth: token.lineWidth,
  lineWidthBold: token.lineWidthBold,
  boxShadow: token.boxShadow,
  boxShadowSecondary: token.boxShadowSecondary,
  boxShadowTertiary: token.boxShadowTertiary,
  motionDurationFast: token.motionDurationFast,
  motionDurationMid: token.motionDurationMid,
  motionDurationSlow: token.motionDurationSlow,
});
